import { auth } from "@/firebase/firebase";
import { nextapi, buildQueryString } from "@/lib/apiRoutes";
import { updateSuggestionStatuses } from "@/lib/suggestions/SuggestionsRepository";

type StructureSummaryOptions = {
  includeItems?: boolean;
};

type PortalSummaryOptions = {
  limit?: number;
  cursor?: string;
  includeEmpty?: boolean;
};

type StructureSuggestionsFallback = {
  suggestions: unknown[];
  __suggestions_meta?: unknown;
};

export type SuggestionsV2MergedResponse = {
  merged?: boolean;
  isMerged?: boolean;
  categories?: unknown[];
  suggestions?: unknown[];
  suggestion_items?: unknown[];
  items?: unknown[];
  [key: string]: unknown;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

export const isServerMergedV2Response = (value: unknown): value is SuggestionsV2MergedResponse =>
  isRecord(value) &&
  (Array.isArray(value.categories) ||
    Array.isArray(value.suggestions) ||
    Array.isArray(value.suggestion_items) ||
    Array.isArray(value.items));

// Use centralized buildQueryString from apiRoutes
// Legacy alias for backward compatibility
const toQueryString = buildQueryString;

const safeReadText = async (res: Response): Promise<string> => {
  try {
    return await res.text();
  } catch {
    return "";
  }
};

const requestJsonWithTimeout = async (
  url: string,
  init: RequestInit | undefined,
  timeoutMs: number
): Promise<unknown> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await requestJson(url, { ...init, signal: controller.signal });
  } catch (err: any) {
    if (err?.name === "AbortError") {
      const timeoutError = new Error("timeout");
      (timeoutError as any).code = "timeout";
      (timeoutError as any).status = 408;
      throw timeoutError;
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
};

const parseJson = async (res: Response): Promise<unknown> => {
  const contentType = res.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) return null;
  try {
    return await res.json();
  } catch {
    return null;
  }
};

const getAuthToken = async (): Promise<string> => {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    const error = new Error("unauthenticated");
    (error as any).code = "unauthenticated";
    throw error;
  }
  return token;
};

const requestJson = async (url: string, init?: RequestInit): Promise<unknown> => {
  const token = await getAuthToken();
  const headers = new Headers(init?.headers || {});
  headers.set("Authorization", `Bearer ${token}`);
  if (init?.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const res = await fetch(url, { ...init, headers });
  const payload = await parseJson(res);
  if (!res.ok) {
    const fallbackText = await safeReadText(res);
    const error = new Error(
      fallbackText || (typeof payload === "string" ? payload : `Request failed (${res.status})`)
    );
    (error as any).status = res.status;
    throw error;
  }
  return payload;
};

/**
 * Get suggestions status/summary for a structure
 * Backoffice route available: /nextapi/structures/[id]/suggestions/status
 * 
 * NOTE: Default reads use /nextapi/structures/{id}/suggestions/status (GET).
 * A server-merged endpoint is available via fetchSuggestionsV2MergedFromService
 * when VITE_SUGGESTIONS_V2_PREFER_SERVER_MERGED=1.
 * The includeItems parameter is ignored as the endpoint always returns available data.
 */
export async function getStructureSuggestionsSummary(
  structureId: string,
  options: StructureSummaryOptions = {}
): Promise<unknown> {
  const normalizedId = String(structureId ?? "").trim();
  if (!normalizedId) return null;
  
  // Use the correct endpoint: /nextapi/structures/{id}/suggestions/status
  const url = nextapi.suggestionsStatus(normalizedId);
  
  try {
    return await requestJson(url);
  } catch (err: any) {
    // Log once without spamming console
    if (err?.status !== 404) {
      console.warn(`[suggestionsV2Client] Failed to fetch suggestions for ${normalizedId}:`, err?.message || String(err));
    }
    throw err;
  }
}

/**
 * Fetch merged suggestions summary (base + overlay statuses) from backoffice service
 * 
 * Endpoint (backoffice): /nextapi/service/structures/{id}/suggestions/summary
 * Query: format=v2&includeStatuses=1
 */
export async function fetchSuggestionsV2MergedFromService(
  structureId: string
): Promise<SuggestionsV2MergedResponse | null> {
  const normalizedId = String(structureId ?? "").trim();
  if (!normalizedId) return null;

  const query = toQueryString({ format: "v2", includeStatuses: 1 });
  const url = `${nextapi.suggestionsMergedSummary(normalizedId)}${query}`;

  try {
    const payload = await requestJsonWithTimeout(url, undefined, 7000);
    return isServerMergedV2Response(payload) ? payload : null;
  } catch (err: any) {
    if (import.meta.env.DEV && err?.status !== 404 && err?.status !== 501 && err?.code !== "timeout") {
      console.warn(
        `[suggestionsV2Client] Failed to fetch server-merged suggestions for ${normalizedId}:`,
        err?.message || String(err)
      );
    }
    return null;
  }
}

/**
 * Get portal suggestions summary
 * 
 * WARNING: The endpoint /nextapi/service/portals/{name}/suggestions/summary may not exist
 * in the backoffice. This function is preserved for compatibility but may return errors.
 * Consider using structure-based queries or Firestore instead.
 * 
 * @deprecated Consider using structure-based queries instead
 */
export async function getPortalSuggestionsSummary(
  portalName: string,
  options: PortalSummaryOptions = {}
): Promise<unknown> {
  const normalizedPortal = String(portalName ?? "").trim();
  if (!normalizedPortal) return null;
  
  const query = toQueryString({
    limit: options?.limit,
    cursor: options?.cursor,
    includeEmpty: options?.includeEmpty,
  });
  
  // NOTE: This endpoint may not exist in current backoffice setup
  const url = `/nextapi/service/portals/${encodeURIComponent(normalizedPortal)}/suggestions/summary${query}`;
  
  try {
    return await requestJson(url);
  } catch (err: any) {
    if (err?.status !== 404) {
      console.warn(`[suggestionsV2Client] Portal endpoint may not exist for ${normalizedPortal}:`, err?.message || String(err));
    }
    throw err;
  }
}

/**
 * Update suggestion item statuses for a structure
 * 
 * NOTE: This function now writes exclusively to the suggestions_status collection (NEW).
 * The old suggestions/{structureId} collection is READ-ONLY in the B2B client.
 * 
 * All writes are delegated to SuggestionsRepository.updateSuggestionStatuses() which uses
 * the new suggestions_status collection with proper permission checks.
 * 
 * Auth context is obtained from auth.currentUser at time of write.
 * 
 * @param structureId - Structure ID
 * @param payload - Should contain { statusByItemId: { [itemId]: status } }
 * @throws Error if update fails
 */
export async function updateSuggestionStatus(
  structureId: string,
  payload: unknown
): Promise<void> {
  const normalizedId = String(structureId ?? "").trim();
  if (!normalizedId) {
    throw new Error("Empty structure ID");
  }
  
  // Extract statusByItemId from payload
  const statusByItemId = 
    payload && typeof payload === "object" && "statusByItemId" in payload
      ? (payload as Record<string, unknown>).statusByItemId
      : null;

  if (!statusByItemId || typeof statusByItemId !== "object") {
    console.error(`[suggestionsV2Client] Invalid payload format for ${normalizedId}`);
    throw new Error("Invalid payload format");
  }

  const result = await updateSuggestionStatuses(
    normalizedId,
    statusByItemId as Record<string, string>
  );
  
  if (!result.success) {
    console.warn(`[suggestionsV2Client] Update failed for ${normalizedId}:`, result.error);
    throw new Error(result.error || "Failed to update suggestion statuses");
  }
}

/**
 * Legacy fallback endpoint for structure suggestions
 * 
 * WARNING: The endpoint /nextapi/structures/{id} may not exist in the backoffice.
 * This function will fail gracefully and return null if the endpoint is unavailable.
 * Callers should handle this and use Firestore or other data sources as fallback.
 * 
 * @deprecated Use Firestore data or suggestionsStatus endpoint instead
 */
export async function getStructureSuggestionsFallback(
  structureId: string
): Promise<StructureSuggestionsFallback | null> {
  const normalizedId = String(structureId ?? "").trim();
  if (!normalizedId) return null;
  
  const url = nextapi.structureDetails(normalizedId);
  
  try {
    const payload = await requestJson(url);
    if (!isRecord(payload)) return null;

    const suggestions = Array.isArray(payload.suggestions)
      ? payload.suggestions
      : Array.isArray(payload.suggestion_items)
        ? payload.suggestion_items
        : Array.isArray(payload.items)
          ? payload.items
          : [];

    return {
      suggestions,
      __suggestions_meta: payload.__suggestions_meta,
    };
  } catch (err: any) {
    // Silently fail for 404s (expected if endpoint doesn't exist)
    if (err?.status === 404) {
      return null;
    }
    console.warn(`[suggestionsV2Client] Fallback endpoint failed for ${normalizedId}:`, err?.message || String(err));
    return null;
  }
}
